import { format } from 'd3-format';
import capitalize from 'lodash/capitalize';

import { formatIntegerToDecimal } from '@revelio/core';
import { ScreenerDimension } from '@revelio/data-access';
import { getCurrencySymbol } from '@revelio/replots';

import { SCREENER_FORMAT_MAP } from './format-map';

export const SCREENER_COLUMN_WHITELIST: string[] = [
  'short_name',
  'headcount',
  'inflow',
  'outflow',
  'hiring',
  'attrition',
  'growth',
  'growth_yoy',
  'salary',
  'tenure',
  'description',
  'email',
  'founder_ethnicity',
  'founder_gender',
  'funding_stage',
  'hq_location',
  'is_public',
  'last_funding_amount',
  'last_funding_year',
  'linkedin_url',
  'phone_number',
  'total_funding_amount',
  'ultimate_parent_name',
  'ultimate_parent_rcid',
  'valuation',
  'website',
  'year_founded',
  'rcid',
  'rics_k10',
  'rics_k50',
  'rics_k400',
];

const SCREENER_RATE_COLUMNS = ['hiring', 'attrition', 'growth'];

const SCREENER_CUSTOM_HEADERS: Record<string, string> = {
  is_public: 'Public Company',
  hq_location: 'HQ Location',
  linkedin_url: 'LinkedIn URL',
  ultimate_parent_name: 'Subsidiary Of',
  ultimate_parent_rcid: 'Subsidiary Of (RCID)',
  rcid: 'RCID',
  rics_k10: 'RICS K10',
  rics_k50: 'RICS K50',
  rics_k400: 'RICS K400',
};

export interface FormatScreenerColumnHeaderOptions {
  skipRateSuffix?: boolean;
}

export function formatScreenerColumnHeader(
  name: string,
  view: ScreenerDimension,
  options?: FormatScreenerColumnHeaderOptions
): string {
  if (name === 'short_name') {
    if (view === ScreenerDimension.Company) {
      return 'Company Name';
    }
    if (view === ScreenerDimension.Geography) {
      return 'Metro Area';
    }
    if (view === ScreenerDimension.Role) {
      return 'Role';
    }
  }

  if (SCREENER_CUSTOM_HEADERS[name]) {
    return SCREENER_CUSTOM_HEADERS[name];
  }

  const words = name.replace(/_/g, ' ').match(/(\([^)]+\)|[^\s]+)/g);

  const parsedName = words
    ?.map((word) => {
      if (word.startsWith('(') && word.endsWith(')')) {
        return word;
      }
      return capitalize(word);
    })
    .join(' ');

  if (parsedName?.includes('Yoy')) {
    return parsedName.replace('Growth Yoy', 'Growth Rate');
  }

  if (parsedName?.includes('Growth')) {
    return parsedName.replace('Growth', 'Cumulative Growth');
  }

  const addRateSuffix =
    !options?.skipRateSuffix &&
    SCREENER_RATE_COLUMNS.some((col) => name.startsWith(col));
  if (addRateSuffix) {
    return `${parsedName} Rate`;
  }

  return parsedName ?? name;
}

function roundValue(value: number): string {
  return Math.round(value).toLocaleString();
}

export function applyScreenerValueFormat(
  value: number | string | boolean | null | undefined,
  columnName: string
): string | number {
  if (value === null || value === undefined) {
    return '';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '';
    }
    value = value[0];
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (
    typeof value !== 'number' ||
    // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
    isNaN(value) ||
    ['rcid', 'ultimate_parent_rcid'].includes(columnName)
  ) {
    return typeof value === 'string' ? value : String(value);
  }

  const isRateColumn = SCREENER_RATE_COLUMNS.some((rateColumn) =>
    columnName.startsWith(rateColumn)
  );

  if (isRateColumn) {
    return formatIntegerToDecimal(value * 100, {
      decimals: 2,
      forceProvidedDecimals: false,
    });
  }

  const rawColumnName = columnName.split('_')[0]?.trim() ?? columnName;

  if (
    ['salary', 'valuation'].includes(rawColumnName) ||
    ['last_funding_amount', 'total_funding_amount'].includes(columnName)
  ) {
    if (value < 1_000_000) {
      return format('.2s')(value);
    }
    return format('.3s')(value).replace('G', 'B');
  }

  if (['headcount', 'inflow', 'outflow'].includes(rawColumnName)) {
    return roundValue(value);
  }

  if (columnName === 'year_founded') {
    if (value === 0) {
      return '';
    }
    return value;
  }

  if (columnName.includes('year')) {
    return value;
  }

  if (columnName.includes('tenure')) {
    return formatIntegerToDecimal(value, { decimals: 1 });
  }

  return formatIntegerToDecimal(value, { decimals: 2 });
}

export interface ApplyCurrencyPrefixOptions {
  /** When true, match FORMAT_MAP key by columnName.includes(key) (screener page behavior). Default: exact or startsWith(key + '_'). */
  matchByIncludes?: boolean;
}

export function applyCurrencyPrefix(
  formattedValue: string | number,
  columnName: string,
  currencyCode: string,
  options?: ApplyCurrencyPrefixOptions
): string | number {
  const lowerColumnName = columnName.toLowerCase();
  const foundKey = options?.matchByIncludes
    ? Object.keys(SCREENER_FORMAT_MAP).find((key) =>
        lowerColumnName.includes(key.toLowerCase())
      )
    : Object.keys(SCREENER_FORMAT_MAP).find((key) => {
        const lowerKey = key.toLowerCase();
        return (
          lowerColumnName === lowerKey ||
          lowerColumnName.startsWith(`${lowerKey}_`)
        );
      });

  if (!foundKey) {
    return formattedValue;
  }

  const formatConfig = SCREENER_FORMAT_MAP[foundKey];
  if (!formatConfig) {
    return formattedValue;
  }

  const symbol =
    formatConfig.text === '$'
      ? getCurrencySymbol(currencyCode)
      : formatConfig.text;

  return formatConfig.type === 'prefix'
    ? `${symbol}${formattedValue}`
    : `${formattedValue}${symbol}`;
}
